import { ApolloLink, Operation } from '@apollo/client';
import { hasDirectives, removeDirectivesFromDocument } from '@apollo/client/utilities';

export const AUTH_REQUIRED_DIRECTIVE = 'authRequired';
export const AUTHORIZED_KEY = 'authorized';
export const AUTH_GUEST_ALLOWED_DIRECTIVE = 'authGuestAllowed';
export const GUEST_AUTHORIZED_KEY = 'guestAuthorized';

/**
 * Accepts an Apollo Operation and will determine if it is auth protected by
 * checking if the `@authRequired` directive is present on the query document.
 *
 * @param {Operation} operation
 * The operation under question
 *
 * @returns {boolean}
 * Whether or not this operation is auth protected
 */
export const isRequestAuthProtected = (operation: Operation): boolean => {
  return hasDirectives([AUTH_REQUIRED_DIRECTIVE], operation.query);
};

/**
 * Accepts an Apollo Operation and will determine if it is accept to use guest auth
 * checking if the `@authGuestAllowed` directive is present on the query document.
 *
 * @param {Operation} operation
 * The operation under question
 *
 * @returns {boolean}
 * Whether or not this operation is allowed for guest authentication
 */
export const isRequestAllowAuthGuest = (operation: Operation): boolean => {
  return hasDirectives([AUTH_GUEST_ALLOWED_DIRECTIVE], operation.query);
};

/**
 * Accepts an Apollo Operation and will determine if the user is logged in when
 * the operation was invoked by checking if the `AUTHORIZED_KEY` exists on the
 * operation context.
 *
 * @param {Operation} operation
 * The operation under question
 *
 * @returns {boolean}
 * Whether or not the user is logged on at the time this operation is invoked
 */
export const isUserLoggedIn = (operation: Operation): boolean => {
  const context = operation.getContext();
  return context[AUTHORIZED_KEY];
};

/**
 * Accepts an Apollo Operation and will determine if the user is logged in when
 * the operation was invoked by checking if the `GUEST_AUTHORIZED_KEY` exists on the
 * operation context.
 *
 * @param {Operation} operation
 * The operation under question
 *
 * @returns {boolean}
 * Whether or not the guest user is logged on at the time this operation is invoked
 */
export const isGuestUserLoggedIn = (operation: Operation): boolean => {
  const context = operation.getContext();
  return context[GUEST_AUTHORIZED_KEY];
};

/**
 * An Apollo Link used to strip the `@authRequired` and `@authGuestAllowed' directive from
 * query documents.
 */
export const stripAuthDirective = new ApolloLink((operation, forward) => {
  const strippedQuery = removeDirectivesFromDocument(
    [{ name: AUTH_REQUIRED_DIRECTIVE }, { name: AUTH_GUEST_ALLOWED_DIRECTIVE }],
    operation.query
  );

  if (strippedQuery) {
    operation.query = strippedQuery;
  }

  return forward(operation);
});
